import { useState } from "react";
import { useSelector } from "react-redux";

import { ContractIcon, SummaryIcon } from "@/assets/icons";
import { MESSAGE } from "@/constants/message";
import { useExecutiveSummary } from "@/hooks/analysisSummary/useExecutiveSummary";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";

import * as Tabs from "@radix-ui/react-tabs";

import { AISummary } from "./AISummary";
import { ReportSummary } from "./ReportSummary";

type AnalysisSummaryWrapperProps = {
  isExpanded: boolean;
  chartRef: React.RefObject<HTMLDivElement>;
  dynamicChartRef: React.RefObject<(HTMLDivElement | null)[]>;
  onSummaryComplete: (summary: string) => void;
  permissionAI: boolean;
};

export const AnalysisSummaryWrapper = ({
  isExpanded,
  chartRef,
  dynamicChartRef,
  onSummaryComplete,
  permissionAI
}: AnalysisSummaryWrapperProps) => {
  const { isAISummaryEnabled } = useAppFeatures();
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const [chunks, setChunks] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(true);
  const { isHasSummary } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  useExecutiveSummary({
    analysisId:
      isAISummaryEnabled && permissionAI ? latestAnalysisId : undefined,
    onDelta: (chunk) => {
      setChunks((prev) => [...prev, chunk.content]);
    },
    onFinal: (full) => {
      setChunks([full.content]);
      setIsStreaming(false);
      onSummaryComplete(full.content);
    }
  });

  if (!isAISummaryEnabled) {
    return (
      <ReportSummary
        className="flex overflow-auto my-4 mx-5 custom-scrollbar pr-2"
        chartRef={chartRef}
        dynamicChartRef={dynamicChartRef}
        isExpanded={isExpanded}
      />
    );
  }

  // Render no summary message when isHasSummary is false
  if (!isHasSummary && permissionAI) {
    return (
      <Tabs.Root
        defaultValue="report"
        className="overflow-auto custom-scrollbar my-4 pr-2"
      >
        <Tabs.List className="grid grid-cols-2 gap-4 border-b border-[#E5E7EB] mb-4 sticky top-0 z-10 bg-white">
          <Tabs.Trigger
            value="report"
            className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
          >
            <div className="flex gap-1 items-center justify-center">
              <ContractIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
              Report Summary
            </div>
          </Tabs.Trigger>

          <Tabs.Trigger
            value="ai"
            className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
          >
            <div className="flex gap-1 items-center justify-center">
              <SummaryIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
              AI Summary
            </div>
          </Tabs.Trigger>
        </Tabs.List>

        {/* Report Summary Tab */}
        <Tabs.Content
          value="report"
          forceMount
          className="data-[state=inactive]:absolute data-[state=inactive]:-left-[0] data-[state=inactive]:-top-[9999px] data-[state=inactive]:w-full"
        >
          <ReportSummary
            className="flex mx-5"
            chartRef={chartRef}
            dynamicChartRef={dynamicChartRef}
            isExpanded={isExpanded}
          />
        </Tabs.Content>

        {/* AI Summary Tab - No Summary Message */}
        <Tabs.Content value="ai">
          <div className="mx-5">
            <div className="mt-3 bg-white-smoke flex-col w-full items-center justify-between text-center p-4 rounded-xl print:mt-4">
              <p className="text-txtBody text-sm font-semibold">
                {MESSAGE.AI_SUMMARY_NO_SCORES_AVAILABLE_TITLE}
              </p>
              <p className="text-txtBody text-xs">
                {MESSAGE.AI_SUMMARY_NO_SCORES_AVAILABLE_DESCRIPTION}
              </p>
            </div>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    );
  }

  // Render tabs with AI Summary when isHasSummary is true
  return (
    <Tabs.Root
      defaultValue="report"
      className="overflow-auto custom-scrollbar my-4 pr-2"
    >
      <Tabs.List className="grid grid-cols-2 gap-4 border-b border-[#E5E7EB] mb-4 sticky top-0 z-10 bg-white">
        <Tabs.Trigger
          value="report"
          className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
        >
          <div className="flex gap-1 items-center justify-center">
            <ContractIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
            Report Summary
          </div>
        </Tabs.Trigger>

        <Tabs.Trigger
          value="ai"
          className="group px-4 py-2 text-sm data-[state=active]:border-b-2 data-[state=active]:border-primary-teal data-[state=active]:text-primary-teal data-[state=active]:font-semibold outline-none"
        >
          <div className="flex gap-1 items-center justify-center">
            <SummaryIcon className="fill-txtBody group-data-[state=active]:fill-primary-teal" />
            AI Summary
          </div>
        </Tabs.Trigger>
      </Tabs.List>

      {/* Report Summary Tab */}
      <Tabs.Content
        value="report"
        forceMount
        className="data-[state=inactive]:absolute data-[state=inactive]:-left-[0] data-[state=inactive]:-top-[9999px] data-[state=inactive]:w-full"
      >
        <ReportSummary
          className="flex mx-5"
          chartRef={chartRef}
          dynamicChartRef={dynamicChartRef}
          isExpanded={isExpanded}
        />
      </Tabs.Content>

      {/* AI Summary Tab */}
      <Tabs.Content value="ai">
        <AISummary
          chunks={chunks}
          isStreaming={isStreaming}
          permissionAI={permissionAI}
        />
      </Tabs.Content>
    </Tabs.Root>
  );
};
